import { observer } from 'mobx-react-lite';
import {
    alreadyExistingUserAdditionFailed,
    anonymousOrDefaultPermissionsDeletionNotPermitted,
    folderPermissionsAddButtonTooltip,
    folderPermissionsDeleteButtonTooltip,
    folderPermissionsColumnTitleForUserNames,
    addFolderPermissionsDialogTitle,
    addFolderPermissionsDialogMessage,
} from './FolderPermissionsList.locstring.json';
import { permissionLevelText } from '../strings.locstring.json';
import loc, { format } from 'owa-localize';
import FolderPermissionsListItem from './FolderPermissionsListItem';
import isDistinguishedPermissionUser from '../helpers/isDistinguishedPermissionUser';
import { getPermissionLevelLocaleString } from '../helpers/permissionLevelLocaleStrings';
import isExistingPermissionUser from '../helpers/isExistingPermissionUser';
import folderPermissionsStore from '../store/store';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { confirm } from 'owa-confirm-dialog';
import { LazyPeoplePickerDialog } from 'owa-people-picker-dialog';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';

import * as React from 'react';
import {
    selectPermissionIndex,
    shouldShowAddUserDialog,
    addPermissionUser,
    deletePermissionUser,
} from '../actions/internalActions';

const styles = require('./FolderPermissionsList.scss');
const classNames = require('classnames');

const addButton = 'Add';
const deleteButton = 'Delete';
const customScrollBarClassName = 'customScrollBar';

/**
 * Component to render the list/table view for all permissions in UI store.
 */
export default observer(function FolderPermissionsList() {
    return (
        <>
            <div className={styles.folderPermissionsAddDel}>
                <IconButton
                    className={styles.permissionIconButton}
                    onClick={openAddUserDialog}
                    title={loc(folderPermissionsAddButtonTooltip)}
                    iconProps={{
                        iconName: addButton,
                    }}
                />
                <IconButton
                    className={styles.permissionIconButton}
                    onClick={deleteSelectedUserPermission}
                    title={loc(folderPermissionsDeleteButtonTooltip)}
                    iconProps={{
                        iconName: deleteButton,
                    }}
                />
            </div>
            <div className={styles.folderPermissionsListContainer}>
                <div className={styles.folderPermissionsListHeaderItem}>
                    <FolderPermissionsListItem
                        name={loc(folderPermissionsColumnTitleForUserNames)}
                        permissionLevel={loc(permissionLevelText)}
                    />
                </div>
                <div
                    className={classNames(
                        customScrollBarClassName,
                        styles.folderPermissionsPeopleList
                    )}>
                    {folderPermissionsStore.permissions.map((permission, permissionIndex) => {
                        // Destructure UserId and get it into variables if permission is defined.
                        const {
                            DistinguishedUser,
                            PrimarySmtpAddress,
                            DisplayName,
                        } = permission?.UserId;
                        return (
                            <FolderPermissionsListItem
                                className={getSelectedUserClass(permissionIndex)}
                                key={DistinguishedUser || PrimarySmtpAddress}
                                name={DistinguishedUser || DisplayName}
                                onClick={onClickUser}
                                permissionIndex={permissionIndex}
                                permissionLevel={getPermissionLevelLocaleString(
                                    permission.PermissionLevel
                                )}
                            />
                        );
                    })}
                </div>
            </div>
            {folderPermissionsStore.showAddUserDialog && (
                <LazyPeoplePickerDialog
                    title={loc(addFolderPermissionsDialogTitle)}
                    subTitle={loc(addFolderPermissionsDialogMessage)}
                    addCallback={addNewUserPermission}
                    cancelCallback={closeAddUserDialog}
                />
            )}
        </>
    );
});

/**
 * Function checks if passed permission is of same user as of selectedPermission in store.
 * @param permissionIndex Passed permission's index.
 * @return Classname if same as selectedPermission else null.
 */
function getSelectedUserClass(permissionIndex: number): string {
    return classNames({
        [styles.folderPermissionsPeopleSelected]:
            permissionIndex === folderPermissionsStore.selectedPermissionIndex,
    });
}

/**
 * This function opens user dialog.
 */
function openAddUserDialog() {
    shouldShowAddUserDialog(true /* shouldShow */);
}

/**
 * This function closes user dialog.
 */
function closeAddUserDialog() {
    shouldShowAddUserDialog(false /* shouldShow */);
}

/**
 * Handler for adding a user permission.
 * Shows confirm dialog if user already exists in view store's permission array, else fires addPermissionUser action if user is not distinguished.
 * @param emailAddressWrapper Object received from add user dialog.
 */
function addNewUserPermission(emailAddressWrapper: EmailAddressWrapper) {
    // Check if user already esists in permissions array of view store, then show confirm dialog and stop delete action.
    if (isExistingPermissionUser(emailAddressWrapper.EmailAddress)) {
        const addUserFailMessage = format(
            loc(alreadyExistingUserAdditionFailed),
            emailAddressWrapper.Name
        );
        confirm('', addUserFailMessage, false, {
            hideCancelButton: true,
        });
    } else {
        addPermissionUser(emailAddressWrapper.EmailAddress, emailAddressWrapper.Name);
    }
}

/**
 * Handler for deleting selected user permission.
 * Shows confirm dialog if user is distinguished else fires deletePermissionUser action if user is not distinguished.
 */
function deleteSelectedUserPermission() {
    // Check if user is distinguished and show confirm dialog and stop delete action.
    if (isDistinguishedPermissionUser(folderPermissionsStore.selectedPermissionIndex)) {
        confirm('', loc(anonymousOrDefaultPermissionsDeletionNotPermitted), false, {
            hideCancelButton: true,
        });
    } else {
        deletePermissionUser();
    }
}

/**
 * On-Click listener for list item which also acts as a user.
 * @param permissionIndex Index of permission for which the clicked user belongs to.
 */
function onClickUser(permissionIndex) {
    selectPermissionIndex(permissionIndex);
}
